import { EVA_RESEARCH_NAME } from "constants/app.constants";

import {
  MCDANIEL_FOLDER_ID,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FOLDER_ID,
  NOVI_FOLDER_NAME,
  NOVI_SEARCH_FOLDER_NAME,
  SYSTEM_DEFAULT_FORECAST_FOLDER_ID,
  WORKING_FORECAST_FOLDER_ID,
  WORKING_FORECAST_FOLDER_NAME
} from "../../../constants";

export const FORECAST_EXPORT_SETTING = "forecastExportSetting";
export const USER_DASHBOARD_SETTING = "userDashboardSetting";
export const USER_DOC_SETTING = "userDocSetting";
// Matches the UserTourSettingController route name (ASP.NET routing is
// case-insensitive, so camelCase here matches UserTourSetting there — same as
// USER_DOC_SETTING above). A mismatch fails silently: the request 404s and the
// read/write treats it as "no record".
export const USER_TOUR_SETTING = "userTourSetting";
export const USER_ABBREVIATIONS_SETTING = "userAbbreviationsSetting";
export const DEFAULT_DECLINE_TYPE_SETTING = "defaultDeclineType";
export const DEFAULT_MIDSTREAM_CHART_BACKGROUND_COLOUR =
  "defaultMidstreamChartBackgroundColour";
export const DEFAULT_PROJECT_SETTING = "defaultProject";
export const DEFAULT_BIN_SETTINGS_SETTING = "defaultBinSettings";
export const DEFAULT_FILTER_SETTING = "defaultFilter";
export const DEFAULT_FACILITY_FILTER_SETTING = "defaultFacilityFilter";
export const DEFAULT_MAP_SETTING = "defaultMapCenter";
export const DEFAULT_FOCUS_TILES_SETTING = "defaultFocusTile";
export const DEFAULT_FACILITY_FOCUS_TILES_SETTING = "defaultFacilityFocusTile";
export const DEFAULT_COLOR_PALETTE_SETTING = "defaultColorPalette";
export const DEFAULT_FACILITY_COLOR_PALETTE_SETTING = "defaultFacilityColorPalette";
export const DEFAULT_TYPE_WELL_DASHBOARD_SETTING = "defaultTypeWellDashboard";
export const DEFAULT_GEO_SETTING = "defaultGeoSettings";
export const USER_MIDSTREAM_SETTINGS = "midstreamSettings";
export const USER_GEO_MODEL_SETTINGS = "geoModelSettings";
export const DEFAULT_MAP_TOOLTIP_FIELDS_SETTING = "defaultMapTooltipField";
export const DEFAULT_FACILITY_MAP_TOOLTIP_FIELDS_SETTING =
  "defaultFacilityMapTooltipField";
export const DEFAULT_FORECAST_SOURCE_SETTING = "defaultForecastSource";
export const UNIT_SETTING = "defaultUnit";
export const DEFAULT_SIGNIFICANT_FIGURES_SETTING = "defaultSignificantFiguresSetting";
export const DEFAULT_AUTO_ZOOM_SETTING = "defaultAutoZoomSetting";
export const DEFAULT_MAP_STYLE_SETTING = "defaultMapStyle";
export const DATA_TABLE_COLUMN_ORDER_SETTING = "DataTableColumnOrder";

export const FORECAST_SOURCES = {
  McDaniel: {
    folderId: MCDANIEL_FOLDER_ID,
    name: EVA_RESEARCH_NAME
  },
  [WORKING_FORECAST_FOLDER_NAME]: {
    folderId: WORKING_FORECAST_FOLDER_ID,
    name: WORKING_FORECAST_FOLDER_NAME
  },
  Novi: {
    folderId: NOVI_FOLDER_ID,
    name: NOVI_FOLDER_NAME
  }
};

// One entry per possible value of the single-valued defaultForecastSource setting.
// Still mutually exclusive by construction: a user has exactly one of these.
// checkedKeys are Forecast-panel tree node ids; specialFolderNames are WIRE values and
// must stay within the backend's strict source enum (see FR-019).
export const FORECAST_SOURCE_CONFIGURATIONS = {
  [MCDANIEL_FOLDER_ID]: {
    checkedKeys: [MCDANIEL_FOLDER_ID],
    specialFolderNames: [MCDANIEL_SEARCH_FOLDER_NAME],
    useMcDaniel: true,
    useWorking: false,
    useNovi: false,
    excludeMcDaniel: false,
    excludeNovi: true
  },
  [WORKING_FORECAST_FOLDER_ID]: {
    checkedKeys: [WORKING_FORECAST_FOLDER_ID],
    specialFolderNames: [WORKING_FORECAST_FOLDER_NAME],
    useMcDaniel: false,
    useWorking: true,
    useNovi: false,
    excludeMcDaniel: true,
    excludeNovi: true
  },
  [NOVI_FOLDER_ID]: {
    checkedKeys: [NOVI_FOLDER_ID],
    specialFolderNames: [NOVI_SEARCH_FOLDER_NAME],
    useMcDaniel: false,
    useWorking: false,
    useNovi: true,
    excludeMcDaniel: true,
    excludeNovi: false
  },
  [SYSTEM_DEFAULT_FORECAST_FOLDER_ID]: {
    checkedKeys: [MCDANIEL_FOLDER_ID, NOVI_FOLDER_ID],
    specialFolderNames: [MCDANIEL_SEARCH_FOLDER_NAME, NOVI_SEARCH_FOLDER_NAME],
    useMcDaniel: true,
    useWorking: false,
    useNovi: true,
    excludeMcDaniel: false,
    excludeNovi: false
  }
};

/**
 * Looks up a forecast-source config by the persisted setting value.
 *
 * The id is a server-supplied string, so a plain `CONFIGS[id]` would resolve inherited
 * members — `constructor`, `toString`, `hasOwnProperty` all return a truthy function, which
 * defeats both a `?? fallback` and an `if (!config) return` guard and hands `undefined`
 * checkedKeys to the store setters. Own-property check first, then fall back to McDaniel so
 * an unrecognised or hostile value degrades to EVA-Research-only instead of throwing.
 */
export function getForecastSourceConfig(forecastFolderId: Maybe<string>) {
  const isKnown =
    typeof forecastFolderId === "string" &&
    Object.prototype.hasOwnProperty.call(
      FORECAST_SOURCE_CONFIGURATIONS,
      forecastFolderId
    );

  return isKnown
    ? FORECAST_SOURCE_CONFIGURATIONS[forecastFolderId]
    : FORECAST_SOURCE_CONFIGURATIONS[MCDANIEL_FOLDER_ID];
}

export const SYSTEM_DEFAULT_FORECAST_SOURCE_NAME = "System Default";

// Synthetic option for the User Settings dropdown — System Default is not a data source,
// so it is not a member of FORECAST_SOURCES (which is indexed by data-source name) and is
// instead pushed in conditionally by the component that renders the dropdown.
export const SYSTEM_DEFAULT_FORECAST_SOURCE_OPTION = {
  value: SYSTEM_DEFAULT_FORECAST_FOLDER_ID,
  label: SYSTEM_DEFAULT_FORECAST_SOURCE_NAME
};
